/**
 * Мини-столбцы «значение по неделям» для плиток «Обзора» — модель без разметки:
 * какой столбец выделить, какие подписи показать, что в подсказке. Рисует
 * `pages/warehouse-dashboard/WeekBars.tsx`.
 *
 * Выбор недель — `pickWeeks` (по флагу `partial` ответа, не по часам браузера):
 * последняя ПОЛНАЯ — основным цветом, предыдущие — приглушённые, текущая
 * неполная — светлая штриховка с подписью «тек.». Значения не пересчитываются.
 */
import type { OperationsWeek } from "@/api/warehouse_dashboard";
import { isoWeekNumber } from "@/lib/warehouse-operations";
import { pickWeeks } from "@/lib/warehouse-overview";

export type BarTone = "last" | "past" | "current";

export interface WeekBar {
  key: string;
  tone: BarTone;
  value: number | null;
  /** Высота относительно максимума окна, 0..1; `null`/0 → 0. */
  ratio: number;
  /** Подпись под столбцом: `dd.mm` начала недели, у текущей — «тек.». */
  label: string;
  /** Показывать ли подпись (при тесноте — через одну, от последней полной). */
  showLabel: boolean;
  /** Подсказка по наведению: `нед. 38, 14–20.09: 21 369 шт`. */
  title: string;
  /** Значение над столбцом — только у выделенного и текущего. */
  valueLabel: string | null;
}

/** Больше стольких подписей в ряд не влезает в плитку (≈ 40 px на подпись). */
export const MAX_BAR_LABELS = 8;

/** `YYYY-MM-DD` → `[dd, mm]` строками, разбором строки (без `new Date`). */
function ddmm(iso: string): [string, string] | null {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(iso);
  return m ? [m[2]!, m[1]!] : null;
}

/** Диапазон недели коротко: `14–20.09`, через месяц — `31.08–06.09`. */
export function fmtWeekSpan(week: OperationsWeek): string {
  const a = ddmm(week.start);
  const b = ddmm(week.end);
  if (!a || !b) return `${week.start}–${week.end}`;
  return a[1] === b[1] ? `${a[0]}–${b[0]}.${b[1]}` : `${a[0]}.${a[1]}–${b[0]}.${b[1]}`;
}

export function weekBars(
  weeks: OperationsWeek[],
  values: (number | null)[],
  fmt: (v: number | null) => string,
  { unit = "", maxLabels = MAX_BAR_LABELS }: { unit?: string; maxLabels?: number } = {},
): WeekBar[] {
  const { last } = pickWeeks(weeks);
  const max = Math.max(0, ...values.map((v) => (v === null ? 0 : v)));
  // Через одну — с шагом, выровненным на последнюю полную: её подпись видна всегда.
  const step = weeks.length > maxLabels ? Math.ceil(weeks.length / maxLabels) : 1;
  const anchor = last ?? weeks.length - 1;
  const suffix = unit ? ` ${unit}` : "";
  return weeks.map((w, i) => {
    const v = values[i] ?? null;
    const tone: BarTone = i === last ? "last" : w.partial ? "current" : "past";
    const d = ddmm(w.start);
    const shown = fmt(v);
    const n = isoWeekNumber(w.start);
    return {
      key: w.start,
      tone,
      value: v,
      ratio: v !== null && v > 0 && max > 0 ? v / max : 0,
      label: w.partial ? "тек." : d ? `${d[0]}.${d[1]}` : w.start,
      showLabel: tone !== "past" || (anchor - i) % step === 0,
      title:
        `${n === null ? "" : `нед. ${n}, `}${fmtWeekSpan(w)}${w.partial ? " (идёт)" : ""}: ` +
        `${shown}${v === null ? "" : suffix}`,
      valueLabel: tone === "past" ? null : shown,
    };
  });
}
